import { Flex, MultiSelect, MultiSelectProps, Text } from "@mantine/core";
import { forwardRef, useMemo, useState } from "react";

import { FKInfoLabel } from "@/features/ui/Base/List/CustomFilter/MultiField";
import { IconX } from "@tabler/icons-react";
import {
  useAuthoritiesMultiAutoComplete,
  useAuthoritiesMultiCode,
} from "./multiAuto-useGetAuthoritiesQuery";

interface AuthorityMultiProps extends React.ComponentPropsWithoutRef<"div"> {
  label: string;
  value: string;
}

export interface AuthoritiesMultiAutoCompleteProps
  extends Partial<MultiSelectProps>,
    Partial<React.RefAttributes<HTMLInputElement>> {
  value: string[];
  onChange?: (e: string[]) => void; // (value: string[]) => React.Dispatch<React.SetStateAction<string>>;
  maxDropdownHeight?: number;
  width?: string;
}

export const AuthoritiesMultiAutoComplete = (params: AuthoritiesMultiAutoCompleteProps) => {
  const { value: Authorities, onChange, maxDropdownHeight, width, ...etcParams } = params;
  const [focused, setFocused] = useState<boolean>(true);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const { data: options } = useAuthoritiesMultiAutoComplete(focused, searchKeyword);
  const { data: initialOptions } = useAuthoritiesMultiCode(!!searchKeyword, searchKeyword ?? null);

  const onChangeHandler = (e: string[]) => {
    onChange && onChange(e);
  };

  const SelectAuthority = forwardRef<HTMLDivElement, AuthorityMultiProps>(
    ({ label: name, value: code, ...others }, ref) => (
      <div ref={ref} {...others}>
        <Flex direction="row" justify="space-between" align="center">
          <Flex align={"center"}>
            <Text>{name}</Text>
            <Text fz="xs">(권한 코드: {code})</Text>
          </Flex>
        </Flex>
      </div>
    )
  );

  // data prop에 들어가는 중복 제거된 data를 useMemo로 감싸기
  const filteredOptions = useMemo(() => {
    const combinedOptions = [
      ...(options ?? []),
      ...(initialOptions ?? []),
      ...(Array.isArray(Authorities) ? Authorities : []).map((value) => ({ label: value, value })),
    ];

    return combinedOptions.reduce(
      (unique: AuthorityMultiProps[], option: AuthorityMultiProps) =>
        unique.some((u) => u.value === option.value) ? unique : [...unique, option],
      []
    );
  }, [options, initialOptions, Authorities]);

  return (
    <MultiSelect
      clearable
      width="100%"
      value={Authorities}
      data={filteredOptions}
      onChange={onChangeHandler}
      searchable
      itemComponent={SelectAuthority}
      searchValue={searchKeyword}
      withinPortal
      onDropdownOpen={() => setFocused(true)}
      onDropdownClose={() => setFocused(false)}
      maxDropdownHeight={maxDropdownHeight ?? 150}
      onSearchChange={setSearchKeyword}
      nothingFound="No Options"
      rightSection={AuthorityInfo({
        Authority: Authorities,
        onChange: onChangeHandler,
      })}
      {...etcParams}
    />
  );
};

const AuthorityInfo = (params: { Authority?: string[]; onChange: (e: string[]) => void }) => {
  const { Authority, onChange } = params;

  const clearHandler: React.MouseEventHandler<SVGSVGElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange([]);
  };

  return Authority?.length ? (
    <FKInfoLabel>
      <IconX size="1rem" onClick={clearHandler} />
    </FKInfoLabel>
  ) : null;
};
